// New-block form. Compliance/Manager-friendly layout (admin-driven
// CVE response should be deliberate, not buried in dense lists).
//
// v160 — added a "Preview impact" dry-run button. The middleware that
// enforces blocks is cache-refreshed every 5 s and is best-effort
// reversible (delete the row), but a too-wide pattern can lock out
// hundreds of legitimate downloads in those 5 s. The preview hits
// /api/admin/security/preview, which runs the same matching logic
// against the artifacts table and returns the projected blast radius
// without persisting anything.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, Eye, AlertCircle, CheckCircle2 } from 'lucide-react';
import { SecurityShell } from '../../components/SecurityShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  createSecurityBlock,
  previewSecurityBlock,
  type BlockType,
  type PreviewResult,
} from '../../api/security';

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

const TYPE_HELP: Record<BlockType, string> = {
  path:
    'Exact match on the request URL path. Most direct — block one specific artifact.',
  pattern:
    'Glob-style wildcard match (POSIX `*`). Block all versions of a package by matching its path prefix, e.g. v3-flatcontainer/leftpad/*',
  sha256:
    'Match on artifact content hash. Survives renames and re-publishes — best for "these exact bytes are malicious".',
  package:
    'Match by package name as a path segment. Blocks every version of a package; slightly heuristic but the right shape for CVE incidents where the maintainer can\'t be trusted.',
};

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function patternPlaceholder(blockType: BlockType): string {
  if (blockType === 'pattern') return 'v3-flatcontainer/leftpad/*';
  if (blockType === 'path') return '/maven/demo/lib/com/example/foo/1.0/foo-1.0.jar';
  if (blockType === 'sha256') return 'a1b2c3...';
  return 'event-stream';
}

function previewButtonLabel(previewing: boolean, hasPreviewed: boolean): string {
  if (previewing) return 'Running dry-run…';
  if (hasPreviewed) return 'Re-run preview';
  return 'Preview impact';
}

type CountTone = 'neutral' | 'danger' | 'warning' | 'accent' | 'success';

function previewCountTone(preview: PreviewResult | null): CountTone {
  if (!preview) return 'neutral';
  if (preview.count_capped) return 'danger';
  if (preview.match_count >= 100) return 'warning';
  if (preview.match_count > 0) return 'accent';
  return 'success';
}

function countToneBadgeTone(tone: CountTone): 'danger' | 'warning' | 'accent' | 'success' {
  if (tone === 'danger') return 'danger';
  if (tone === 'warning') return 'warning';
  if (tone === 'accent') return 'accent';
  return 'success';
}

function previewCardClassName(preview: PreviewResult): string {
  if (preview.match_count === 0) return 'border-success/40 bg-success/5';
  if (preview.count_capped) return 'border-danger/40 bg-danger/5';
  if (preview.match_count >= 100) return 'border-warning/40 bg-warning/5';
  return 'border-accent/40 bg-accent/5';
}

function previewTitle(preview: PreviewResult): string {
  if (preview.match_count === 0) return 'Preview: no matches';
  if (preview.count_capped) {
    return `Preview: ${preview.match_count.toLocaleString()}+ artifacts (count capped)`;
  }
  return `Preview: ${preview.match_count.toLocaleString()} artifact${preview.match_count === 1 ? '' : 's'} would be blocked`;
}

function previewDescription(preview: PreviewResult): string {
  if (preview.count_capped) {
    return 'The dry-run probe stops counting at 10 000 — the real number is higher. Almost certainly a too-wide pattern.';
  }
  if (preview.match_count === 0) {
    return 'Nothing in the registry matches this pattern right now. Block will only affect future uploads.';
  }
  return 'Below: the artifacts that would return 403 if you create this block now.';
}

function PreviewPanel({ preview, countTone }: Readonly<{ preview: PreviewResult; countTone: CountTone }>) {
  return (
    <Card className={previewCardClassName(preview)}>
      <CardHeader
        title={previewTitle(preview)}
        description={previewDescription(preview)}
        actions={
          <Badge tone={countToneBadgeTone(countTone)}>
            {preview.count_capped
              ? '10 000+'
              : preview.match_count.toLocaleString()}
          </Badge>
        }
      />

      {preview.would_overlap && preview.would_overlap.length > 0 && (
        <CardBody className="border-b border-border">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
            <div className="text-sm text-fg-muted">
              <span className="text-fg font-medium">
                {preview.would_overlap.length} existing block
                {preview.would_overlap.length === 1 ? '' : 's'} already cover{preview.would_overlap.length === 1 ? 's' : ''} this pattern.
              </span>
              <ul className="mt-1.5 space-y-1">
                {preview.would_overlap.map((o) => (
                  <li key={o.id} className="font-mono text-xs">
                    <span className="text-fg-muted">{o.block_type}</span>
                    <span className="mx-1.5">·</span>
                    <span className="text-fg">{o.pattern}</span>
                    {o.reason && (
                      <span className="text-fg-subtle"> — {o.reason}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardBody>
      )}

      {preview.by_repo.length > 0 && (
        <CardBody className="border-b border-border">
          <p className="orb-section-title text-fg-muted mb-2">By repository</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border">
                  <th className="py-1.5 pr-3 font-medium text-fg-muted">Project</th>
                  <th className="py-1.5 pr-3 font-medium text-fg-muted">Repo</th>
                  <th className="py-1.5 pr-3 font-medium text-fg-muted">Format</th>
                  <th className="py-1.5 pr-3 font-medium text-fg-muted text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {preview.by_repo.map((r) => (
                  <tr key={r.repo_id} className="border-b border-border/40 last:border-0">
                    <td className="py-1.5 pr-3 text-fg">{r.project_slug}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs text-accent">{r.repo_slug}</td>
                    <td className="py-1.5 pr-3"><span className="orb-code text-[10px]">{r.format}</span></td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-fg">{r.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      )}

      {preview.sample.length > 0 && (
        <CardBody>
          <p className="orb-section-title text-fg-muted mb-2">
            Sample matches ({preview.sample.length}{preview.match_count > preview.sample.length && ` of ${preview.count_capped ? '10 000+' : preview.match_count.toLocaleString()}`})
          </p>
          <ul className="space-y-1 max-h-80 overflow-y-auto">
            {preview.sample.map((m) => (
              <li
                key={m.artifact_id}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-bg-hover/40 text-xs"
                title={m.url}
              >
                <span className="font-mono text-fg flex-1 truncate">{m.url}</span>
                {m.version && (
                  <span className="text-fg-muted shrink-0">{m.version}</span>
                )}
                <span className="text-fg-subtle shrink-0 tabular-nums">{bytes(m.size_bytes)}</span>
              </li>
            ))}
          </ul>
        </CardBody>
      )}
    </Card>
  );
}

export function SecurityNew() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  // After Preview runs successfully, we treat Create as the primary
  // action. Before that, Preview is primary and Create is secondary
  // — the operator should see the blast radius before committing.
  const [hasPreviewed, setHasPreviewed] = useState(false);
  const [form, setForm] = useState({
    block_type: 'pattern' as BlockType,
    pattern: '',
    reason: '',
    customer_message: '',
    cve_id: '',
    blocked_until: '',
    active: true,
  });

  // When the pattern or block_type changes, the previously-rendered
  // preview is stale — clear it so the operator can't be fooled into
  // creating a block based on outdated dry-run output.
  const updateForm = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === 'pattern' || k === 'block_type') {
      setPreview(null);
      setHasPreviewed(false);
    }
  };

  const onPreview = async () => {
    if (!form.pattern) {
      setErr('Pattern is required to preview.');
      return;
    }
    setPreviewing(true);
    setErr(null);
    try {
      const result = await previewSecurityBlock({
        block_type: form.block_type,
        pattern: form.pattern,
      });
      setPreview(result);
      setHasPreviewed(true);
    } catch (e) {
      setErr(String(e));
    } finally {
      setPreviewing(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await createSecurityBlock({
        ...form,
        blocked_until: form.blocked_until ? new Date(form.blocked_until).toISOString() : null,
      });
      navigate('/security');
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  // Toned label for the count tile based on size of impact.
  const countTone = previewCountTone(preview);

  return (
    <SecurityShell
      eyebrow="Enforcement · new rule"
      title="New security block"
      description="Block a specific artifact, content hash, or whole package from being downloaded across the platform."
    >
      <div className="max-w-3xl mx-auto w-full">
        <form onSubmit={onSubmit} className="space-y-4">
          <Card>
            <CardHeader title="Block type & pattern" />
            <CardBody className="space-y-4">
              <div>
                <label htmlFor="block-type" className="block text-sm font-medium text-fg mb-1.5">
                  Block type
                </label>
                <select
                  id="block-type"
                  className={selectClass + ' w-full'}
                  value={form.block_type}
                  onChange={(e) => updateForm('block_type', e.target.value as BlockType)}
                >
                  <option value="pattern">pattern — wildcard path match</option>
                  <option value="path">path — exact path match</option>
                  <option value="sha256">sha256 — content hash match</option>
                  <option value="package">package — by package name</option>
                </select>
                <p className="text-xs text-fg-muted mt-1.5">{TYPE_HELP[form.block_type]}</p>
              </div>
              <div>
                <label htmlFor="block-pattern" className="block text-sm font-medium text-fg mb-1.5">
                  Pattern
                </label>
                <Input
                  id="block-pattern"
                  required
                  value={form.pattern}
                  onChange={(e) => updateForm('pattern', e.target.value)}
                  placeholder={patternPlaceholder(form.block_type)}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  type="button"
                  variant={hasPreviewed ? 'secondary' : 'primary'}
                  onClick={onPreview}
                  disabled={previewing || !form.pattern}
                >
                  <Eye className="h-4 w-4" />
                  {previewButtonLabel(previewing, hasPreviewed)}
                </Button>
                <p className="text-xs text-fg-muted">
                  Runs the match against the artifacts table without persisting anything.
                </p>
              </div>
            </CardBody>
          </Card>

          {/* Preview panel — only renders once a dry-run has completed. */}
          {preview && <PreviewPanel preview={preview} countTone={countTone} />}

          <Card>
            <CardHeader title="Context" description="Shown to clients in the 403 response and recorded in the audit log." />
            <CardBody className="space-y-4">
              <div>
                <label htmlFor="block-reason" className="block text-sm font-medium text-fg mb-1.5">
                  Reason <span className="text-fg-subtle font-normal">(plain text)</span>
                </label>
                <Input
                  id="block-reason"
                  required
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="e.g. malicious post-install script — see GHSA-xxxx-xxxx"
                />
              </div>
              <div>
                <label htmlFor="block-cve-id" className="block text-sm font-medium text-fg mb-1.5">
                  CVE / advisory ID <span className="text-fg-subtle font-normal">(optional)</span>
                </label>
                <Input
                  id="block-cve-id"
                  value={form.cve_id}
                  onChange={(e) => setForm({ ...form, cve_id: e.target.value })}
                  placeholder="CVE-2024-12345 or GHSA-xxxx-xxxx-xxxx"
                  className="font-mono text-sm"
                />
              </div>
              {/* v185 — long-form developer-facing message shown
                  verbatim in build-tool logs and on the public
                  /blocked/<id> landing page. */}
              <div>
                <label htmlFor="block-customer-message" className="block text-sm font-medium text-fg mb-1.5">
                  Developer message <span className="text-fg-subtle font-normal">(optional, shown to clients)</span>
                </label>
                <textarea
                  id="block-customer-message"
                  value={form.customer_message}
                  onChange={(e) => setForm({ ...form, customer_message: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm font-mono"
                  placeholder={`Optional multi-line context shown verbatim to anyone hitting this block.\n\nExample:\nUpgrade to log4j 2.17.x or higher.\nSee runbook: https://wiki.example.com/log4shell\nQuestions? #platform-help`}
                />
                <p className="text-xs text-fg-muted mt-1.5">
                  Surfaced in the 403 response body (Maven, npm, curl
                  output) and on the public <code className="orb-code">/blocked/&lt;id&gt;</code> page.
                  Leave empty to use the Reason above.
                </p>
              </div>
              <div>
                <label htmlFor="block-blocked-until" className="block text-sm font-medium text-fg mb-1.5">
                  Blocked until <span className="text-fg-subtle font-normal">(optional auto-expiry)</span>
                </label>
                <Input
                  id="block-blocked-until"
                  type="datetime-local"
                  value={form.blocked_until}
                  onChange={(e) => setForm({ ...form, blocked_until: e.target.value })}
                />
                <p className="text-xs text-fg-muted mt-1.5">
                  Leave empty to keep the block active indefinitely until manually disabled.
                </p>
              </div>
            </CardBody>
          </Card>

          <div className="rounded-md bg-warning/5 border border-warning/30 p-4 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
            <div className="text-sm text-fg-muted">
              <span className="font-medium text-fg">Effect is immediate.</span>{' '}
              Once saved, every fetch attempt matching the pattern returns
              <code className="orb-code ml-1">403 Forbidden</code> within seconds
              (the middleware cache refreshes every 5s). Run{' '}
              <span className="text-fg font-medium">Preview impact</span> first
              if you're not certain about the pattern's blast radius.
            </div>
          </div>

          {err && (
            <Card className="border-danger/30 bg-danger/5">
              <CardBody>
                <p className="text-danger font-mono text-sm">{err}</p>
              </CardBody>
            </Card>
          )}

          <div className="flex justify-end gap-2 items-center">
            {hasPreviewed && (
              <span className="text-xs text-fg-muted flex items-center gap-1.5 mr-auto">
                <CheckCircle2 className="h-3 w-3 text-success" />
                Preview reviewed
              </span>
            )}
            <Button type="button" variant="secondary" onClick={() => navigate('/security')}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={hasPreviewed ? 'primary' : 'secondary'}
              disabled={busy}
            >
              <ShieldAlert className="h-4 w-4" />
              Create block
            </Button>
          </div>
        </form>
      </div>
    </SecurityShell>
  );
}
